import { type FieldMetadata, useInputControl } from '@conform-to/react'
import { type SelectValueProps as SelectValuePrimitiveProps } from '@radix-ui/react-select'
import { Flex, Select as RadixSelect } from '@radix-ui/themes'

import { type TFunction } from 'i18next'
import * as React from 'react'
import { mergeRefs } from 'react-merge-refs'
import { FormFieldError } from '#app/components/ui/form-field-error.tsx'

/* -------------------------------------------------------------------------------------------------
 * Select
 * -----------------------------------------------------------------------------------------------*/

type SelectElement = React.ElementRef<typeof RadixSelect.Trigger>
type SelectProps = React.ComponentPropsWithoutRef<typeof RadixSelect.Root> &
	Pick<
		React.ComponentPropsWithoutRef<typeof RadixSelect.Content>,
		'container'
	> & {
		meta: FieldMetadata<string>
		Label?: React.ReactNode
		placeholder: SelectValuePrimitiveProps['placeholder']
		t: TFunction<string, undefined>
	}

const Select = React.forwardRef<SelectElement, SelectProps>(
	(props, forwardedRef) => {
		const { children, Label, placeholder, meta, t } = props

		const selectRef =
			React.useRef<React.ElementRef<typeof RadixSelect.Trigger>>(null)
		const control = useInputControl(meta)

		return (
			<>
				<Flex direction="column" gapY="2" align="start">
					<RadixSelect.Root
						name={meta.name}
						defaultValue={meta.initialValue}
						value={control.value || meta.initialValue}
						onValueChange={control.change}
						onOpenChange={(open) => {
							if (!open) {
								control.blur()
							}
						}}
					>
						<Flex
							direction={{ initial: 'column', xs: 'row' }}
							justify="between"
							gap="4"
							align="center"
						>
							{!!Label && Label}
							<RadixSelect.Trigger
								ref={mergeRefs([selectRef, forwardedRef])}
								placeholder={placeholder as string}
								aria-label={meta.name}
							/>
							<RadixSelect.Content>{children}</RadixSelect.Content>
						</Flex>
					</RadixSelect.Root>
					{!!meta.errors && <FormFieldError error={meta.errors[0]} t={t} />}
				</Flex>
			</>
		)
	},
)
Select.displayName = 'Select'

/* -----------------------------------------------------------------------------------------------*/

export { Select }
